import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

export interface OpenPanelOptions {
  prefillInput?: string;
  autoSubmit?: boolean;
}

interface AiSlidePanelContextValue {
  isOpen: boolean;
  panelOptions: OpenPanelOptions | null;
  open: (options?: OpenPanelOptions) => void;
  close: () => void;
  toggle: (options?: OpenPanelOptions) => void;
  clearPanelOptions: () => void;
}

const AiSlidePanelContext = createContext<AiSlidePanelContextValue | undefined>(
  undefined,
);

export const AiSlidePanelProvider = memo(function AiSlidePanelProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelOptions, setPanelOptions] = useState<OpenPanelOptions | null>(
    null,
  );

  const open = useCallback((options?: OpenPanelOptions) => {
    if (options) {
      setPanelOptions(options);
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setPanelOptions(null);
  }, []);

  const toggle = useCallback((options?: OpenPanelOptions) => {
    setIsOpen((prev) => {
      if (!prev && options) {
        setPanelOptions(options);
      }
      if (prev) {
        setPanelOptions(null);
      }
      return !prev;
    });
  }, []);

  const clearPanelOptions = useCallback(() => {
    setPanelOptions(null);
  }, []);

  return (
    <AiSlidePanelContext.Provider
      value={{ isOpen, panelOptions, open, close, toggle, clearPanelOptions }}
    >
      {children}
    </AiSlidePanelContext.Provider>
  );
});

export function useAiSlidePanel(): AiSlidePanelContextValue {
  const context = useContext(AiSlidePanelContext);
  if (!context) {
    throw new Error(
      "useAiSlidePanel must be used within an AiSlidePanelProvider",
    );
  }
  return context;
}
